/**
 * Search logging - Stage 5.
 *
 * Records what shoppers type into the site search so search can be improved: which
 * queries return nothing, which words are misspelled, what "boots" actually means to
 * a customer. Nothing here affects what the shopper sees.
 *
 * DESIGN NOTES, all of them the result of an adversarial review that found nine
 * blockers in the first version. Please read before changing anything.
 *
 * 1. THE QUERY NEVER GOES IN THE URL. It is POSTed in the body, always. Writing
 *    fetch(url + '?q=' + query) would put customer search text into every access log
 *    between here and Supabase, permanently, outside our control. This is the
 *    "simplification" someone makes in six months. Do not.
 *
 * 2. PERSONAL DATA IS STRIPPED HERE, BEFORE SENDING - not at the far end. Shoppers
 *    paste order references, emails and postcodes into search boxes. Redacting server
 *    side is too late: the raw string would already have crossed the network and could
 *    land in a log. The same rules run again in Postgres, because this file is
 *    bypassable - that is belt and braces, not duplication to remove.
 *
 * 3. THE CARD CHECK RUNS BEFORE THE LENGTH CAP. Truncating first can split a card
 *    number into a fragment that no longer looks like one, which would then be stored
 *    as ordinary text.
 *
 * 4. fetch({keepalive:true}), NOT navigator.sendBeacon. A beacon cannot set headers,
 *    and this endpoint needs an apikey header. A beacon would be rejected while
 *    returning true - silent total failure with nothing to debug.
 *
 * 5. CONSENT FAILS CLOSED. Shopify's privacy API loads asynchronously, so early
 *    keystrokes can see "undefined". No answer means no send.
 *
 * 6. THE SETTLE DELAY IS SEPARATE FROM THE SEARCH THROTTLE. main-search.js uses 120ms
 *    to decide when to ASK Shopify - that is a measured speed win, do not touch it.
 *    This file waits far longer to decide the shopper has finished typing. Without a
 *    second timer, "fly spray" logs nine rows and the data is unusable.
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://vvlqdescgpckjxyocdcj.supabase.co/rest/v1/rpc/log_search_events';
  // Publishable key. Public by design - it can call one function and read nothing.
  // Verified: this key cannot read search_events, orders, order_lines or product_costs.
  var KEY = 'sb_publishable_LpyuLcIwE6w1tqCTdJ_KGQ_MRldV3QP';

  var SETTLE_MS = 1200;      // how long after typing stops before we call it a query
  var MAX_PER_PAGE = 20;     // stop logging after this many events in one page view
  var MAX_QUERY = 120;
  var FLUSH_MS = 2500;       // send shortly after an event, not only in batches of 5

  var buffer = [];
  var sent = 0;
  var settleTimer = null;
  var lastLogged = '';
  var flushTimer = null;

  // --- consent -------------------------------------------------------------
  // Undefined means "not answered yet", which must behave as no.
  //
  // 2026-09-15 (Aaron, after publishing): the store shows Shopify's consent
  // banner and most UK visitors never answer it, so a consent-only log saw
  // nothing. What is recorded now depends on the answer:
  //   no consent  - the search words (redacted) and the result counts only.
  //                 No session id, no click events, and nothing is written to
  //                 the device (sessionId() is never called). A row is a bare
  //                 "someone searched X and saw N results" with no identifier,
  //                 which is aggregate product analytics, not tracking.
  //   consent     - the full record: a per-visit session id (sessionStorage,
  //                 random, per visit) and the product clicks, so searches can
  //                 be grouped by visit and click-learning works.
  function consentGiven() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return false;
      return cp.analyticsProcessingAllowed() === true;
    } catch (e) {
      return false;
    }
  }

  // --- redaction -----------------------------------------------------------
  // Mirrors search_analytics.redact() in Postgres. Keep the two in step.
  function redact(raw) {
    if (!raw) return '';
    var s = String(raw).toLowerCase().trim();
    s = s.replace(/[\x00-\x1F\x7F]/g, '');           // control characters
    s = s.replace(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/g, '[email]');
    // Card-shaped: 12+ digits allowing spaces or hyphens. MUST run before the plain
    // digit rule and before the length cap - "4111 1111 1111 1111" is four runs of
    // four and the 6-digit rule alone does not see it.
    s = s.replace(/(?:\d[ -]?){10,}/g, '[number]');   // 10+: cards AND spaced UK phone numbers
    s = s.replace(/\b[a-z]{1,2}\d[a-z\d]? ?\d[a-z]{2}\b/g, '[postcode]');
    s = s.replace(/\d{6,}/g, '[number]');                   // order refs, phone numbers
    s = s.replace(/[<>]/g, '');                             // never becomes markup
    s = s.replace(/^[=+@-]+/, '');                          // never a live Excel formula
    return s.slice(0, MAX_QUERY).trim();
  }

  // --- session -------------------------------------------------------------
  // Random, per visit, not a customer identifier. Groups one visit's searches so we
  // can tell "searched twice then clicked" from "two different people".
  function sessionId() {
    try {
      var k = 'jhr_sl';
      var v = sessionStorage.getItem(k);
      if (!v) {
        v = (Math.random().toString(36) + Math.random().toString(36)).replace(/[^a-z0-9]/g, '').slice(0, 16);
        sessionStorage.setItem(k, v);
      }
      return v;
    } catch (e) {
      return '';                                            // private mode - fine
    }
  }

  function isPreview() {
    try {
      if (window.Shopify && window.Shopify.theme && window.Shopify.theme.role) {
        return window.Shopify.theme.role !== 'main';
      }
    } catch (e) { /* fall through */ }
    return /preview_theme_id=/.test(window.location.search);
  }

  function device() {
    return (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) ? 'mobile' : 'desktop';
  }

  // --- sending -------------------------------------------------------------
  function flush() {
    if (!buffer.length) return;
    var batch = buffer.splice(0, MAX_PER_PAGE);
    try {
      fetch(ENDPOINT, {
        method: 'POST',
        keepalive: true,                                    // survives the page unloading
        headers: { 'apikey': KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: batch })
      }).catch(function () { /* logging must never surface to a shopper */ });
    } catch (e) { /* same */ }
  }

  function queue(evt) {
    if (sent >= MAX_PER_PAGE) return;
    var consent = consentGiven();
    if (!consent && evt.type === 'click') return;           // clicks need consent
    sent += 1;
    evt.sid = consent ? sessionId() : '';                    // no consent: no id, no storage
    evt.preview = isPreview();
    buffer.push(evt);
    // Send on a short timer as well as at 5 events. Batching alone is not enough:
    // a shopper who searches once and leaves would sit in the buffer waiting for a
    // fifth event that never comes, leaving pagehide as the only route out - and
    // pagehide is unreliable, worst of all on mobile Safari. The failure mode is
    // "a quiet day for searches" rather than anything that looks broken.
    if (buffer.length >= 5) {
      flush();
    } else if (!flushTimer) {
      flushTimer = setTimeout(function () { flushTimer = null; flush(); }, FLUSH_MS);
    }
  }

  // --- public API used by main-search.js -----------------------------------
  window.jhrSearchLog = {

    /**
     * Called by main-search.js every time results are rendered - which is on every
     * keystroke. This does NOT log; it restarts the settle timer, so only the query
     * the shopper actually stopped on is recorded.
     */
    /**
     * `total` is the TRUE match count from the full search engine (capped at 1000
     * by Shopify). Since 2026-09-14 the dropdown's first page comes from that
     * engine, so the settled row carries shown + total together - one row per
     * query the shopper stopped on, never one per keystroke.
     */
    // `fixed` (29 Sep 2026): the words the results were shown for when the search corrected what was typed
    // ("Showing results for Dodson & Horrell") - so the Englander AI app can say what it fixed by itself.
    onResults: function (query, shown, total, fixed) {
      var q = redact(query);
      if (!q || q.length < 2) return;
      var f = fixed ? redact(fixed) : '';
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(function () {
        if (q === lastLogged) return;                       // same query, already logged
        lastLogged = q;
        var ev = {
          q: q,
          shown: shown,
          total: (total >= 0) ? total : null,
          zero: shown === 0,
          device: device(),
          type: 'search',
          src: 'dropdown'
        };
        if (f && f !== q) ev.fixed = f;
        queue(ev);
      }, SETTLE_MS);
    },

    onClick: function (query, handle, title, position, src) {
      var q = redact(query);
      if (!q) return;
      queue({
        q: q,
        handle: handle,
        ptitle: title,
        pos: position,
        device: device(),
        type: 'click',
        src: src === 'results_page' ? 'results_page' : 'dropdown'
      });
      flush();                                              // page is about to navigate
    },

    /**
     * The results page (/search?q=). 2026-09-16: it had never logged anything.
     * Clicks are logged with src 'results_page' (position = place in the grid).
     * The search itself is only logged when the shopper did NOT come through
     * the dropdown - i.e. arrived from outside the site - because the dropdown
     * already recorded it when they stopped typing.
     */
    // `typed` (29 Sep 2026): the words as typed when the page shows corrected ones (?orig=) - the search is then
    // logged as typed, with `fixed` = the words shown; clicks stay on the words shown, as before.
    onResultsPage: function (query, shown, total, fromOutside, typed) {
      var q = redact(query);
      if (!q || q.length < 2) return;
      if (fromOutside) {
        var t = typed ? redact(typed) : '';
        var ev = { q: t || q, shown: shown, total: (total >= 0) ? total : null, zero: shown === 0,
                   device: device(), type: 'search', src: 'results_page' };
        if (t && t !== q) ev.fixed = q;
        queue(ev);
      }
      document.addEventListener('click', function (e) {
        var block = e.target && e.target.closest && e.target.closest('.collection-listing .product-grid > .product-block');
        if (!block) return;
        try {
          var link = block.querySelector('a[href*="/products/"]');
          var href = link ? link.getAttribute('href') : '';
          var handle = href ? (href.split('/products/')[1] || '').split('?')[0].split('#')[0] : '';
          var titleEl = block.querySelector('.product-block__title');
          var siblings = Array.prototype.slice.call(block.parentNode.querySelectorAll(':scope > .product-block'));
          window.jhrSearchLog.onClick(q, handle, titleEl ? titleEl.textContent.trim() : '', siblings.indexOf(block) + 1, 'results_page');
        } catch (err) { /* never break a shopper's click */ }
      }, true);
    }
  };

  // A click on a result navigates away, so send whatever is pending.
  document.addEventListener('click', function (e) {
    var block = e.target && e.target.closest && e.target.closest('.main-search-result');
    if (!block) return;
    try {
      var link = block.querySelector('a[href*="/products/"]');
      var titleEl = block.querySelector('.product-block__title');
      var href = link ? link.getAttribute('href') : '';
      var handle = href ? (href.split('/products/')[1] || '').split('?')[0].split('#')[0] : '';
      var input = document.querySelector('.main-search__input');
      // Rank: main-search.js stamps data-pos (the strip holds ranks 1-10 and the
      // list carries on from 11, so a sibling count alone would be 10 short);
      // the sibling count is the fallback for older markup.
      var stamped = parseInt(block.getAttribute('data-pos') || '', 10);
      var siblings = Array.prototype.slice.call(
        block.parentNode ? block.parentNode.querySelectorAll('.main-search-result') : []);
      window.jhrSearchLog.onClick(
        input ? input.value : '',
        handle,
        titleEl ? titleEl.textContent.trim() : '',
        stamped > 0 ? stamped : siblings.indexOf(block) + 1  // rank computed at click time
      );
    } catch (err) { /* never break a shopper's click */ }
  }, true);

  ['pagehide', 'visibilitychange'].forEach(function (evt) {
    window.addEventListener(evt, function () {
      if (evt === 'visibilitychange' && document.visibilityState !== 'hidden') return;
      if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
      if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
      flush();
    });
  });
})();
